import debug from 'debug';
import { components } from '../connections/integro-api-types';
import { Application, Poller, downloads } from '@integro/core';

const log = debug('itp:integro.helpers.ArtifactDownloader');

const OP_READ_ARTIFACT_JOB = 'read_artifact_job';
const OP_DOWNLOAD_ARTIFACT = 'download_artifact';
const POLL_INTERVAL = 1000;

type CreateArtifactJob = components['schemas']['CreateArtifactJob'];

export class ArtifactDownloader {
  app: Application;
  poller?: Poller;
  jobData?: CreateArtifactJob;
  error?: any;

  isPending: boolean = false;
  isCompleted: boolean = false;
  isRunning: boolean = false;
  isErrored: boolean = false;
  uiSkipComponentReload: boolean = true;

  onError: CallableFunction | null;

  constructor(app: Application, onError: CallableFunction = null) {
    this.app = app;
    this.jobData = null;
    this.error = null;
    this.onError = onError;
  }

  begin(job: Promise<CreateArtifactJob>) {
    log('begin job');
    this.isRunning = true;

    Promise.resolve(job)
      .then((d) => this.initJob(d))
      .catch((err) => this.updateState(null, err));
  }

  stop() {
    this.poller?.stop();
  }

  private initJob(job: CreateArtifactJob) {
    this.poller = new Poller({
      name: 'ArtifactDownloader',
      callback: this.pollerCallback.bind(this),
      interval: POLL_INTERVAL,
      errback: (err) => this.onError && this.onError(err),
    });
    this.updateState(job, null);
    log('poller started');
  }

  async loadJob(artifact_id: string) {
    return this.app.connections.request(OP_READ_ARTIFACT_JOB, {
      parameters: { id: artifact_id },
    });
  }

  private async pollerCallback() {
    try {
      // ERROR FROM SERVER: operation not found, request not defined.
      const resp = await this.app.connections.request(OP_READ_ARTIFACT_JOB, {
        parameters: { id: this.jobData!.artifact_id },
      });
      return this.updateState(resp.data as CreateArtifactJob, null);
    } catch (err) {
      return this.updateState(null, err);
    }
  }

  private updateState(data: CreateArtifactJob, error: Error | null = null) {
    try {
      if (data) {
        this.jobData = data;
        this.isPending = data.status === 'pending';
        this.isCompleted = data.status === 'completed';
      }

      this.setErrorStates(error);

      if (this.isPending && this.poller) {
        this.poller.start();
      }

      if (this.isRunning && !this.poller.isRunning) {
        this.poller.start();
      }

      if (
        (this.isCompleted || this.isErrored) &&
        this.poller &&
        this.poller.isRunning
      ) {
        this.poller.stop();
        if (data) {
          this.downloadArtifact(data);
        }
      }

      if (error && this.onError) {
        this.onError(error);
      }

      log(
        'state changed job: %o, error: %o, downloader: %o',
        this.jobData,
        error,
        this,
      );
    } catch (err) {
      this.setErrorStates(err);
      if (this.onError) {
        return this.onError(err);
      }
      console.error(err);
    }
  }

  private setErrorStates(error: Error | null) {
    if (error) {
      this.error = error;
      this.isErrored = true;
      this.isRunning = false;
    } else {
      this.error = null;
      this.isErrored = null;
    }
  }

  private complete() {
    this.isPending = false;
    this.isCompleted = true;
    this.isRunning = false;
    this.isErrored = false;
    this.error = null;
  }

  private downloadArtifact(data: CreateArtifactJob) {
    const url = this.app.connections.urlFor(OP_DOWNLOAD_ARTIFACT, {
      id: data.artifact_id,
    });
    Promise.resolve(downloads.getFileBLOB(url, data.filename))
      .then((blob) => downloads.downloadFile(blob.file, blob.fileName))
      .then(() => this.complete())
      .catch((err) => {
        this.setErrorStates(err);
        if (this.onError) {
          return this.onError(err);
        }
        console.error(err);
      });
  }
}
